import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import Image from 'components/Image';
import { ButtonV3 } from 'components/ComponentV2';
import IconButton from 'components/IconButton';
import { useResponsive } from 'hooks/useResponsive';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from './SplashScreen.module.scss';

interface SplashScreenProps {
  isProfileComplete: boolean;
  onClose: () => void;
}

const SplashScreen: React.FC<SplashScreenProps> = (props) => {
  const router = useRouter();
  const [t] = useTranslation('candidate.cover-letter');
  const screens = useResponsive();
  const { isProfileComplete, onClose } = props;

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const isCompleteProfileStep = useMemo(
    () => currentIndex === 3,
    [currentIndex]
  );

  const stepsData = useMemo(() => {
    const steps = [
      {
        title: t('splash-screen.step-1.title'),
        imageSrc: '/images/cover_letter_builder_1.png',
        stepInfo: t('splash-screen.step-1.step-info'),
        actionLabel: t('splash-screen.step-1.action-label'),
      },
      {
        title: t('splash-screen.step-2.title'),
        imageSrc: '/images/cover_letter_builder_2.png',
        stepInfo: t('splash-screen.step-2.step-info'),
      },
      {
        title: t('splash-screen.step-3.title'),
        imageSrc: '/images/cover_letter_builder_3.png',
        stepInfo: t('splash-screen.step-3.step-info'),
      },
    ];

    const profileStep = {
      title: t('splash-screen.profile-step.title'),
      imageSrc: '/images/complete_profile_image.png',
      stepInfo: t('splash-screen.profile-step.step-info'),
    };

    return isProfileComplete ? steps : [...steps, profileStep];
  }, [isProfileComplete]);

  const changeStepIndex = (step: number) => () => {
    if (step > stepsData.length - 1) {
      onClose();
    } else {
      setCurrentIndex(step);
    }
  };

  const goToEditProfile = () => {
    router.push(PAGE_ROUTES.CANDIDATE_PROFILE);
    onClose();
  };

  return (
    <div className={styles.CoverLetterSplashScreen}>
      <div className={styles.splashScreen}>
        <div
          className={cn(styles.splashScreenHeader, {
            [styles.completeProfileHeader]: isCompleteProfileStep,
          })}
        >
          <p>{stepsData[currentIndex].title}</p>
          {isCompleteProfileStep && (
            <IconButton
              iconName="close"
              size="small"
              className={styles.closeIcon}
              onClick={onClose}
            />
          )}
        </div>
        <div className={styles.splashScreenContent}>
          <Image
            src={stepsData[currentIndex].imageSrc}
            height={screens.sm ? 137 : 180}
          />
          <p className={styles.stepInfo}>{stepsData[currentIndex].stepInfo}</p>
          {!isCompleteProfileStep && (
            <div className={styles.stepCounter}>
              {[1, 2, 3].map((_, index) => (
                <div
                  key={index}
                  className={cn(styles.stepCounterStep, {
                    [styles.active]: index === currentIndex,
                  })}
                ></div>
              ))}
            </div>
          )}
        </div>
        <div className={styles.splashScreenAction}>
          {currentIndex > 0 && !isCompleteProfileStep && (
            <ButtonV3
              color="secondary"
              isFullWidth
              onClick={changeStepIndex(currentIndex - 1)}
            >
              {t('splash-screen.previous')}
            </ButtonV3>
          )}
          <ButtonV3
            isFullWidth
            onClick={
              isCompleteProfileStep
                ? goToEditProfile
                : changeStepIndex(currentIndex + 1)
            }
          >
            {isCompleteProfileStep
              ? t('splash-screen.complete-profile')
              : t('splash-screen.next')}
          </ButtonV3>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
